import { createContext, useContext } from "react";
import type { ApiSchema } from "../../../api/apiSchema";
import { itemReferenceAt, readChangeSetHead, type ItemEdit } from "../../../api/content/changeSetsApi";
import { parseDocumentVersionRef } from "../../../shared/references/contentReferences";
import { newItemId } from "../../../shared/references/itemIds";

/**
 * Which item a draft saves (AU-3): a new item with the ID the form chose, or the next
 * version of an existing one, made on the version the user started from. An existing one
 * says whether it becomes this project's copy where the project doesn't hold it, such as a
 * library's (ADR-0037 D7): `null` where module B can't continue such a copy when it is saved
 * again, so the server refuses a library's item and the error says where it is changed.
 */
export type ItemTarget =
  | { readonly itemId: string; readonly base: null }
  | {
      readonly itemId: string;
      /** The `VersionRef` the draft started from. */
      readonly base: string;
      readonly derivation: Derivation | null;
    };

type ExistingItem = { readonly itemId: string; readonly reference: string } | null;

/**
 * A step's or Workflow's target, made once per form so a save tried again derives under the
 * same copy ID: a new item, or `base` changed as the Run's chosen Workflow version runs it,
 * if any. Module B continues only these kinds' copies: they record their source.
 */
export function derivingTargetOf(base: ExistingItem, chosenWithin: string | null): ItemTarget {
  return base === null ? { itemId: newItemId(), base: null } : { itemId: base.itemId, base: base.reference, derivation: derivationOf(chosenWithin, null) };
}

/** Another kind's target: a new item, or the next version of `base`, never a copy. */
export function replacingTargetOf(base: ExistingItem): ItemTarget {
  return base === null ? { itemId: newItemId(), base: null } : { itemId: base.itemId, base: base.reference, derivation: null };
}

/**
 * A draft of a version this project doesn't hold, such as a library's, or one the person chose
 * to derive (F33), becomes this project's copy: the server makes it under `itemId` and moves
 * each Workflow on the way to `within` to it.
 */
export interface Derivation {
  readonly itemId: string;
  /** The Workflow version whose uses change; `null` makes only the copy. */
  readonly within: string | null;
  /** The person chose Derive: a copy even where the project holds the item. */
  readonly chosen: boolean;
}

/**
 * The Workflow version the person chose to derive within (F33): the Run's, carried by the
 * "Edit an item" step that the Run's Derive opened. `null` for any other edit.
 */
export const DerivationRootContext = createContext<string | null>(null);

export function useDerivationRoot(): string | null {
  return useContext(DerivationRootContext);
}

/** The derivation a form's draft of an existing item saves as, where it is one. */
export function derivationOf(chosenWithin: string | null, otherwiseWithin: string | null): Derivation {
  return chosenWithin === null ? { itemId: newItemId(), within: otherwiseWithin, chosen: false } : { itemId: newItemId(), within: chosenWithin, chosen: true };
}

/**
 * The edit that saves `content` as `target`, for a commit on `head` (`null`: the first
 * commit of a new change set, made on `main`). An existing item is replaced on the version
 * it has at that head, so earlier edits of the same change set stay; if the change set
 * removed it, the version the user started from is named and the server says why. A draft
 * the target derives is derived: where the person chose it, or where the project holds the
 * item neither at that head nor on `main`. Saved again, as after a save whose answer failed,
 * it is derived again under the same copy ID, which the server continues (ADR-0037 D7).
 */
export async function itemEditAt(projectId: string, head: string | null, target: ItemTarget, content: ApiSchema<"ItemContent">): Promise<ItemEdit> {
  const atHead = head === null ? null : await itemReferenceAt(projectId, head, target.itemId);
  if (target.base === null && atHead === null) {
    return { operation: "create", item_id: target.itemId, content };
  }
  if (target.base !== null && target.derivation !== null && (target.derivation.chosen || (atHead === null && (await itemReferenceAt(projectId, null, target.itemId)) === null))) {
    return { operation: "derive", source: target.base, content, within: target.derivation.within, item_id: target.derivation.itemId };
  }
  const shown = target.base === null ? null : parseDocumentVersionRef(target.base)?.version ?? null;
  const basedOn = (atHead === null ? null : parseDocumentVersionRef(atHead)?.version) ?? shown;
  return { operation: "replace", item_id: target.itemId, content, based_on: basedOn };
}

/** The version an item has at a change set's head now, such as the step a form just saved. */
export async function referenceInChangeSet(projectId: string, changeSetId: string, itemId: string): Promise<string | null> {
  const head = await readChangeSetHead(changeSetId);
  return itemReferenceAt(projectId, head, itemId);
}

/** The version a target's draft has in the change set: the copy's where it was derived, else the item's own. */
export async function savedReference(projectId: string, changeSetId: string, target: ItemTarget): Promise<string | null> {
  const copy = target.base === null || target.derivation === null ? null : await referenceInChangeSet(projectId, changeSetId, target.derivation.itemId);
  return copy ?? referenceInChangeSet(projectId, changeSetId, target.itemId);
}
